import type { UtilisationBand } from '../../../utils/utilisation';
import {
  Box,
  Link,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import { useMemo, useState } from 'react';
import { numericSx } from '../../../theme/typography';
import { PersonAvatar } from '../../shared/PersonAvatar';
import { utilTone } from '../utils/utilTone';
import { PanelCard } from './PanelCard';
import { LeaveKey } from './LeaveKey';
import { LoggedUtilisationLink } from './LoggedUtilisationLink';

export interface ConsultantUtilisationRow {
  id: string;
  name: string;
  /** Owned-avatar URL (`/api/images/:id`); absent → initials fallback. */
  avatarUrl?: string;
  /** 0–200ish. The bar fills to 100% and caps there; the value carries the truth (the % text). */
  utilisation: number;
  /** The server's band for `utilisation`; the row's tone. */
  band: UtilisationBand;
  /** No available capacity this period (fully on leave) — show "On leave", not 0%. */
  onLeave?: boolean;
  /** Approved-leave business days in the period — flags partial leave so a high % isn't misread. */
  leaveDays?: number;
  /** Active allocations overlapping the period (assigned even if on leave). */
  allocationCount?: number;
}

/** "1 day" / "1.5 days" — half-days kept to one decimal. */
const fmtDays = (d: number): string => `${Number.isInteger(d) ? d : d.toFixed(1)} day${d === 1 ? '' : 's'}`;

interface ConsultantUtilisationListProps {
  rows: ConsultantUtilisationRow[];
  /** Optional explicit total — defaults to `rows.length`. Used for the "+ N more" footer. */
  totalCount?: number;
  /** Target band displayed in the footer note ("target 80–110%"). */
  target?: [number, number];
  /** Maximum rows shown before the "+ N more" footer (prototype default = 8). */
  maxRows?: number;
  loading?: boolean;
  onViewAll?: () => void;
  /** Optional click handler for individual rows (navigate to the person, etc.). */
  onRowClick?: (row: ConsultantUtilisationRow) => void;
  /** Draws the line pointing at logged utilisation; pass `useCanOpenLoggedUtilisation()`. */
  showLoggedUtilisationLink?: boolean;
}

type SortMode = 'most' | 'least' | 'all';

/**
 * Exact-prototype port of `UtilList`
 * (the v2 prototype, lines 479-511).
 *
 * Layout is a load-bearing 3-column CSS grid: `170px | 1fr | 44px`
 * (name-cell · soft-palette bar · right-aligned tabular %). The bar's
 * fill comes from `palette.bars[N]` via the `utilTone` band helper
 * (bar-2 mint < 80 · bar-5 moss 80–110 · bar-6 rose > 110); the
 * saturated tone is reserved for the % numeric text only. 100% fills the
 * bar completely; over-allocation caps the fill at full and is read from the
 * band colour + the % text (matching the Planning Board summary cards).
 *
 * Replaces the old `ConsultantPerformanceChart` (MUI X BarChart) which
 * diverged from the prototype's row-list aesthetic.
 */
export function ConsultantUtilisationList({
  rows,
  totalCount,
  target = [80, 110],
  maxRows = 8,
  loading,
  onViewAll,
  onRowClick,
  showLoggedUtilisationLink = false,
}: ConsultantUtilisationListProps) {
  const [sortMode, setSortMode] = useState<SortMode>('most');

  const visible = useMemo(() => {
    const sorted = [...rows].sort((a, b) => {
      if (sortMode === 'least') return a.utilisation - b.utilisation;
      return b.utilisation - a.utilisation;
    });
    return sortMode === 'all' ? sorted : sorted.slice(0, maxRows);
  }, [rows, sortMode, maxRows]);

  const moreCount = (totalCount ?? rows.length) - visible.length;

  return (
    <PanelCard
      title="Planned utilisation"
      action={
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <ToggleButtonGroup
            value={sortMode}
            exclusive
            onChange={(_, v: SortMode | null) => v && setSortMode(v)}
            size="small"
            aria-label="Sort consultants by planned utilisation"
            sx={{
              '& .MuiToggleButton-root': {
                fontSize: '0.6875rem',
                px: 1,
                py: 0.25,
                fontWeight: 600,
                color: 'text.secondary',
                border: (theme) => `1px solid ${theme.palette.divider}`,
                '&.Mui-selected': {
                  color: 'primary.main',
                  backgroundColor: (theme) => theme.palette.accent.tint,
                },
              },
            }}
          >
            <ToggleButton value="most">Most</ToggleButton>
            <ToggleButton value="least">Least</ToggleButton>
            <ToggleButton value="all">All</ToggleButton>
          </ToggleButtonGroup>
          {onViewAll ? (
            <Link
              component="button"
              type="button"
              onClick={onViewAll}
              underline="hover"
              sx={{ fontSize: '0.8125rem' }}
            >
              View all
            </Link>
          ) : null}
        </Stack>
      }
      data-testid="widget-consultant-utilisation"
    >
      {visible.length === 0 ? (
        <Typography
          sx={{ color: 'text.secondary', fontSize: '0.8125rem', py: 2 }}
        >
          {loading ? 'Loading…' : 'No planned utilisation to display.'}
        </Typography>
      ) : (
        <Stack spacing="9px">
          {visible.map((row) => (
            <UtilisationRow key={row.id} row={row} onClick={onRowClick} />
          ))}
          {moreCount > 0 ? (
            <Typography
              sx={{
                fontSize: '0.6875rem',
                color: 'text.disabled',
                mt: '6px',
              }}
            >
              + {moreCount} more consultant{moreCount === 1 ? '' : 's'} — target{' '}
              {target[0]}–{target[1]}%
            </Typography>
          ) : (
            <Typography
              sx={{
                fontSize: '0.6875rem',
                color: 'text.disabled',
                mt: '6px',
              }}
            >
              target {target[0]}–{target[1]}%
            </Typography>
          )}
        </Stack>
      )}
      {visible.length > 0 && <LeaveKey />}
      {showLoggedUtilisationLink && (
        <Box sx={{ mt: 1 }}>
          <LoggedUtilisationLink />
        </Box>
      )}
    </PanelCard>
  );
}

interface UtilisationRowProps {
  row: ConsultantUtilisationRow;
  onClick?: (row: ConsultantUtilisationRow) => void;
}

function UtilisationRow({ row, onClick }: UtilisationRowProps) {
  const tone = utilTone(row.band);
  // 100% fills the bar; over-allocation caps at full and reads from the band
  // colour + the % text (consistent with the board summary cards).
  const barWidthPct = row.utilisation <= 0 ? 0 : Math.min(row.utilisation, 100);

  const clickable = !!onClick;

  return (
    <Box
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? () => onClick!(row) : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick!(row);
              }
            }
          : undefined
      }
      sx={{
        display: 'grid',
        gridTemplateColumns: '170px 1fr 44px',
        columnGap: '10px',
        alignItems: 'center',
        cursor: clickable ? 'pointer' : undefined,
        outline: 'none',
        '&:hover .util-name': clickable ? { color: 'primary.main' } : {},
      }}
    >
      {/* Col 1 — avatar + name */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          minWidth: 0,
        }}
      >
        <PersonAvatar name={row.name} src={row.avatarUrl} size={22} fontSize="0.625rem" />
        <Typography
          className="util-name"
          sx={{
            fontSize: '0.75rem',
            color: 'text.primary',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {row.name}
        </Typography>
        {(row.onLeave || (row.leaveDays ?? 0) > 0) && (
          <Tooltip
            title={
              row.onLeave
                ? `On approved leave · ${row.allocationCount ?? 0} active allocation${(row.allocationCount ?? 0) === 1 ? '' : 's'}`
                : `Booked ${row.utilisation}% of available capacity · ${fmtDays(row.leaveDays ?? 0)} leave this period · ${row.allocationCount ?? 0} active allocation${(row.allocationCount ?? 0) === 1 ? '' : 's'}`
            }
          >
            <BeachAccessIcon
              titleAccess={row.onLeave ? 'On leave' : 'Has leave this period'}
              sx={(theme) => ({ fontSize: 12, color: theme.palette.statusInk.info, flexShrink: 0, opacity: row.onLeave ? 1 : 0.7 })}
            />
          </Tooltip>
        )}
      </Box>

      {/* Col 2 — soft palette bar */}
      <Box
        sx={(theme) => ({
          position: 'relative',
          height: 8,
          backgroundColor: theme.palette.surface.sunk,
          borderRadius: 4,
          overflow: 'hidden',
        })}
      >
        {row.onLeave ? (
          // On leave: a soft blue hatch (no capacity) rather than an empty bar
          // that reads as "idle, allocate now".
          <Box
            sx={(theme) => ({
              position: 'absolute',
              inset: 0,
              backgroundColor: theme.palette.statusSoft.info,
              backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 3px, ${theme.palette.statusInk.info} 3px, ${theme.palette.statusInk.info} 4px)`,
              opacity: 0.7,
            })}
          />
        ) : (
          <Box
            sx={(theme) => ({
              width: `${barWidthPct}%`,
              height: '100%',
              borderRadius: 4,
              backgroundColor: theme.palette.bars[tone.fillIdx].fill,
            })}
          />
        )}
      </Box>

      {/* Col 3 — right-aligned tabular % (or "On leave") */}
      {row.onLeave ? (
        <Typography
          sx={(theme) => ({
            // Match the heatmap's "On leave" sizing (0.6875rem) — this is a
            // primary status value, not a decorative micro-label.
            fontSize: '0.6875rem',
            fontWeight: 600,
            textAlign: 'right',
            color: theme.palette.statusInk.info,
            whiteSpace: 'nowrap',
          })}
        >
          On leave
        </Typography>
      ) : (
        <Typography
          sx={[
            numericSx,
            {
              fontSize: '0.75rem',
              fontWeight: 600,
              textAlign: 'right',
              color: tone.textColor,
            },
          ]}
        >
          {row.utilisation}%
        </Typography>
      )}
    </Box>
  );
}
